import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserInfo, UserInfo as AuthUserInfo } from '../../utils/auth';
import { 
  X, 
  Bell, 
  Home,
  User, 
  Users, 
  Briefcase, 
  ChevronRight,
  ChevronLeft,
  Settings,
  LogOut,
  ChevronDown,
  FileText
} from 'lucide-react';

// Types
interface UserInfo {
  accountId: string;
  userName: string;
  userId: string;
  role: string;
}

interface SidebarProps {
  isDesktopSidebarOpen: boolean;
  setIsDesktopSidebarOpen: (open: boolean) => void;
  isMobileSidebarOpen: boolean;
  setIsMobileSidebarOpen: (open: boolean) => void;
  isProfileDropdownOpen: boolean;
  setIsProfileDropdownOpen: (open: boolean) => void;
  activeMenu: string;
  setActiveMenu: (menu: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  isDesktopSidebarOpen,
  setIsDesktopSidebarOpen,
  isMobileSidebarOpen,
  setIsMobileSidebarOpen,
  isProfileDropdownOpen,
  setIsProfileDropdownOpen,
  activeMenu,
  setActiveMenu
}) => {
  const navigate = useNavigate();

  // Get actual user info from auth
  const authUserInfo = getUserInfo();
  
  // Format account code to 2-4-4 format
  const formatAccountCode = (code: string): string => {
    if (!code || code === 'Unknown') return 'Unknown';
    const cleaned = code.replace(/[^A-Z0-9]/g, ''); // Remove non-alphanumeric characters
    if (cleaned.length >= 10) {
      return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 6)}-${cleaned.slice(6, 10)}`;
    }
    return code; // Return original if not long enough
  };
  
  const accountId = authUserInfo ? formatAccountCode(authUserInfo.accountCode || 'Unknown') : 'Unknown';

  // Check if screen is mobile size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileSidebarOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setIsMobileSidebarOpen]);


  const menuItems = [
    { id: 'home', label: '홈', icon: <Home className="w-5 h-5" /> },
    { id: 'announcements', label: '플랫폼 공지사항', icon: <Bell className="w-5 h-5" /> },
    { id: 'account', label: '계정', icon: <Briefcase className="w-5 h-5" /> },
    { id: 'users', label: '사용자', icon: <Users className="w-5 h-5" /> },
    { id: 'projects', label: '프로젝트', icon: <FileText className="w-5 h-5" /> }
  ];

  const handleMenuClick = (menuId: string): void => {
    setActiveMenu(menuId);
    
    switch (menuId) {
      case 'home':
        navigate('/main');
        break;
      case 'users':
        navigate('/users');
        break;
      case 'projects':
        navigate('/projects');
        break;
      default:
        alert('준비중입니다');
        break;
    }
  };

  return (
    <>
      {/* Mobile Sidebar Overlay */}
      {isMobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Desktop/Tablet */}
      <aside className={`
        hidden lg:flex
        ${isDesktopSidebarOpen ? 'w-64' : 'w-16'}
        bg-white border-r border-gray-200 transition-all duration-300 flex-col relative
      `}>
        {/* Logo Section */}
        <div className="h-16 border-b border-gray-200 flex items-center justify-between px-4">
          {isDesktopSidebarOpen && (
            <div className="flex items-center">
              <img src="/flos-icon.svg" alt="Flos" className="w-8 h-8 mr-2" />
              <span className="text-xl font-semibold text-gray-900">Flos</span>
            </div>
          )}
          {!isDesktopSidebarOpen && (
            <div className="w-full flex justify-center">
              <img src="/flos-icon.svg" alt="Flos" className="w-8 h-8" />
            </div>
          )}
        </div>

        {/* Toggle Button for Desktop */}
        <button
          onClick={() => setIsDesktopSidebarOpen(!isDesktopSidebarOpen)}
          className="absolute -right-3 top-20 w-6 h-6 bg-white border border-gray-300 rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
        >
          {isDesktopSidebarOpen ? (
            <ChevronLeft className="w-3 h-3 text-gray-600" />
          ) : (
            <ChevronRight className="w-3 h-3 text-gray-600" />
          )}
        </button>

        {/* Menu Items */}
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => handleMenuClick(item.id)}
                  className={`w-full flex items-center ${
                    isDesktopSidebarOpen ? 'px-3 py-2' : 'p-2 justify-center'
                  } rounded-lg transition-colors ${
                    activeMenu === item.id
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title={!isDesktopSidebarOpen ? item.label : undefined}
                >
                  {item.icon}
                  {isDesktopSidebarOpen && <span className="ml-3">{item.label}</span>}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom Section */}
        {isDesktopSidebarOpen && (
          <div className="p-4 border-t border-gray-200">
            <div className="text-xs text-gray-500">
              <p>계정 ID: {accountId}</p>
              <p className="mt-1">v1.0.0</p>
            </div>
          </div>
        )}
      </aside>

      {/* Sidebar - Mobile */}
      <aside className={`
        fixed lg:hidden inset-y-0 left-0 z-50
        ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        w-64 bg-white border-r border-gray-200 transition-all duration-300 flex flex-col
      `}>
        {/* Logo Section */}
        <div className="h-16 border-b border-gray-200 flex items-center justify-between px-4">
          <div className="flex items-center">
            <img src="/flos-icon.svg" alt="Flos" className="w-8 h-8 mr-2" />
            <span className="text-xl font-semibold text-gray-900">Flos</span>
          </div>
          <button
            onClick={() => setIsMobileSidebarOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Menu Items */}
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => {
                    handleMenuClick(item.id);
                    setIsMobileSidebarOpen(false);
                  }}
                  className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors ${
                    activeMenu === item.id
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {item.icon}
                  <span className="ml-3">{item.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom Section */}
        <div className="p-4 border-t border-gray-200">
          <div className="text-xs text-gray-500">
            <p>계정 ID: {accountId}</p>
            <p className="mt-1">v1.0.0</p>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;